import React, { useState, useEffect } from "react";
import {
  Typography,
  Card,
  CardContent,
  Box,
  CircularProgress,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
} from "@mui/material";
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Notifications as NotificationsIcon,
} from "@mui/icons-material";
import { ScorekeeperRequest } from "../types";
import { apiRequest } from "../config/api";

function ScorekeeperRequests() {
  const [requests, setRequests] = useState<ScorekeeperRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    requestId: string;
    action: "approve" | "reject";
    username: string;
  }>({ open: false, requestId: "", action: "approve", username: "" });
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const response = await apiRequest("/api/admin/scorekeeper-requests", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setRequests(data);
      } else {
        console.error("Failed to fetch scorekeeper requests");
      }
    } catch (error) {
      console.error("Error fetching scorekeeper requests:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = (
    requestId: string,
    action: "approve" | "reject",
    username: string
  ) => {
    setConfirmDialog({
      open: true,
      requestId,
      action,
      username,
    });
  };

  const confirmAction = async () => {
    const { requestId, action } = confirmDialog;

    try {
      setProcessing(requestId);
      const token = localStorage.getItem("authToken");
      const response = await apiRequest(
        `/api/admin/scorekeeper-requests/${requestId}/${action}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        setSnackbar({
          open: true,
          message: `Scorekeeper request ${action}d successfully!`,
          severity: "success",
        });
        await fetchRequests(); // Refresh the list
      } else {
        const errorData = await response.json();
        setSnackbar({
          open: true,
          message: errorData.error || `Failed to ${action} request`,
          severity: "error",
        });
      }
    } catch (error) {
      console.error(`Error ${action}ing request:`, error);
      setSnackbar({
        open: true,
        message: `Network error while ${action}ing request`,
        severity: "error",
      });
    } finally {
      setProcessing(null);
      setConfirmDialog({
        open: false,
        requestId: "",
        action: "approve",
        username: "",
      });
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-GB", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 4, display: "flex", alignItems: "center", gap: 2 }}>
        <NotificationsIcon sx={{ fontSize: 32, color: "primary.main" }} />
        <Typography variant="h4" fontWeight="bold">
          Scorekeeper Requests
        </Typography>
        {requests.length > 0 && (
          <Chip
            label={`${requests.length} pending`}
            color="warning"
            variant="filled"
          />
        )}
      </Box>

      {requests.length === 0 ? (
        <Card sx={{ textAlign: "center", py: 6 }}>
          <CardContent>
            <NotificationsIcon
              sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
            />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Pending Requests
            </Typography>
            <Typography variant="body2" color="text.secondary">
              When users register as scorekeepers, their requests will appear
              here for approval.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper} elevation={3}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "primary.light" }}>
                <TableCell sx={{ fontWeight: "bold", color: "white" }}>
                  Username
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", color: "white" }}>
                  Full Name
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", color: "white" }}>
                  Request Date
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", color: "white" }}>
                  Status
                </TableCell>
                <TableCell
                  sx={{ fontWeight: "bold", color: "white" }}
                  align="center"
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.map((request) => (
                <TableRow key={request.id} hover>
                  <TableCell>
                    <Typography fontWeight="medium">
                      {request.username}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {request.firstName} {request.lastName}
                  </TableCell>
                  <TableCell>
                    {formatDate(request.createdAt.toString())}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={request.status.toUpperCase()}
                      color="warning"
                      variant="outlined"
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Box
                      sx={{ display: "flex", gap: 1, justifyContent: "center" }}
                    >
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        startIcon={<ApproveIcon />}
                        onClick={() =>
                          handleAction(request.id, "approve", request.username)
                        }
                        disabled={processing === request.id}
                        sx={{ minWidth: 100 }}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        size="small"
                        startIcon={<RejectIcon />}
                        onClick={() =>
                          handleAction(request.id, "reject", request.username)
                        }
                        disabled={processing === request.id}
                        sx={{ minWidth: 100 }}
                      >
                        Reject
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}
      >
        <DialogTitle>
          Confirm{" "}
          {confirmDialog.action === "approve" ? "Approval" : "Rejection"}
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to {confirmDialog.action} the scorekeeper
            request for <strong>{confirmDialog.username}</strong>?
          </Typography>
          {confirmDialog.action === "approve" && (
            <Alert severity="info" sx={{ mt: 2 }}>
              This will grant scorekeeper access to this user, allowing them to
              score matches.
            </Alert>
          )}
          {confirmDialog.action === "reject" && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This will deny scorekeeper access. The user can submit a new
              request later.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setConfirmDialog((prev) => ({ ...prev, open: false }))
            }
            disabled={processing !== null}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmAction}
            variant="contained"
            color={confirmDialog.action === "approve" ? "success" : "error"}
            disabled={processing !== null}
          >
            {processing === confirmDialog.requestId
              ? "Processing..."
              : `${confirmDialog.action === "approve" ? "Approve" : "Reject"}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ScorekeeperRequests;
